import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import {
    AnimatedActionBarModeTransition,
    BackButton,
    EventsExportSelectionBar,
} from '@/components';
import {
    useAddEventsToReportAction,
    useEventsExport,
    useGridMultiSelection,
} from '@/hooks';
import { EventsBrowseBar } from '@/features/BrowseBar';
import { EventsListLayout } from '@/features/EventsListLayout';
import { EventsListCards } from './EventsListCards';
import { EVENTS_CONFIG } from './constants';
import { useEventsSortDefault } from './useEventsPageFilters';
import type { EventsPageScenario } from './types';
import { SidePanel } from '@/components/SidePanel';

interface EventsPageProps {
    scenario: EventsPageScenario;
}

export function EventsPage({ scenario }: EventsPageProps) {
    const { t } = useTranslation([I18nNamespace.EVENTS, I18nNamespace.COMMON]);
    const defaultSortOption = useEventsSortDefault();
    const selection = useGridMultiSelection();
    const eventsExport = useEventsExport({
        onStarted: selection.exitMultiSelectMode,
    });
    const addToReportAction = useAddEventsToReportAction({
        events: scenario.items,
        selection,
    });
    const page =
        scenario.pagination.mode === 'paginated'
            ? scenario.pagination.page
            : null;
    const { clearSelection } = selection;
    useEffect(() => {
        clearSelection();
    }, [page, clearSelection]);

    if (scenario.error) {
        return (
            <div className="h-full flex items-center justify-center">
                <p className="text-red-400">{t('page.errorLoading')}</p>
            </div>
        );
    }

    const visibleEventIds = scenario.items.map((item) => item.event.id);

    const selectionTotal =
        scenario.pagination.mode === 'loadMore' && scenario.pagination.hasMore
            ? undefined
            : scenario.eventCount;

    return (
        <div className="relative h-full min-h-0">
            <SidePanel side="left">{scenario.filterView}</SidePanel>
            {scenario.showBackButton && (
                <BackButton
                    variant="floating"
                    label={scenario.backButtonLabel}
                />
            )}
            <EventsListLayout
                pageKey={scenario.pageKey}
                eventCount={scenario.eventCount}
                isEmpty={scenario.isEmpty}
                sourceCount={scenario.sourceCount}
                query={null}
                isLoading={scenario.isLoading}
                pagination={scenario.pagination}
                actionBar={
                    <AnimatedActionBarModeTransition
                        isMultiSelectMode={selection.isMultiSelectMode}
                        browseSlot={
                            <EventsBrowseBar
                                config={EVENTS_CONFIG}
                                initialSortOption={defaultSortOption}
                                onEnterMultiSelect={
                                    selection.enterMultiSelectMode
                                }
                            />
                        }
                        selectionSlot={
                            <EventsExportSelectionBar
                                selection={selection}
                                visibleEventIds={visibleEventIds}
                                totalCount={selectionTotal}
                                entityLabel={t('common:pageTitle.events')}
                                exportBusy={eventsExport.busy}
                                onExport={eventsExport.startExport}
                                bulkActions={[addToReportAction]}
                            />
                        }
                    />
                }
                emptyMessage={scenario.emptyMessage}
                displayMode="events"
            >
                <EventsListCards
                    items={scenario.items}
                    onEventClick={scenario.onEventClick}
                    selectable={selection.isMultiSelectMode}
                    selectedEventIds={selection.selectedIds}
                    onSelectToggle={selection.toggleSelected}
                    eventExport={eventsExport}
                />
            </EventsListLayout>
        </div>
    );
}
